/** Additive kinetic aura on the front of the player sprite during Punchmore attacks. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import { ensureSoftCircleTexture, ensureSpikeTexture } from '../ui/CrispUtil';
import type { AttackState } from './CombatComponent';
import { getFistPosition } from '../utils/PlayerGeometry';
import type { Position } from '../utils/Types';

const CRISP_SCALE = 2;
const INV_CRISP = 1 / CRISP_SCALE;

interface SpikeConfig {
  count: number;
  width: number;
  texHeight: number;
  minLength: number;
  maxLength: number;
  alpha: number;
  shimmerHz: number;
  shimmerDepth: number;
  spinSpeed: number;
}

interface GlowConfig {
  radius: number;
  color: string;
  alpha: number;
  spikes?: SpikeConfig;
}

interface SpikeState {
  image: Phaser.GameObjects.Image;
  baseAngle: number;
  scaleY: number;
  shimmerPhase: number;
}

/** Soft aura bloom in the facing direction during Punchmore windup/active phases. Canvas 2D circle with additive blend. */
export class PunchmoreGlowComponent implements Component {
  private scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Sprite;
  private facing: Position;
  private glowImage: Phaser.GameObjects.Image | null = null;
  private spikes: SpikeState[] = [];
  private config: GlowConfig;
  private currentAlpha = 0;
  private targetAlpha = 0;
  private elapsed = 0;

  constructor(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter,
    facing: Position,
  ) {
    this.scene = scene;
    this.sprite = sprite;
    this.facing = facing;

    // Load glow config from punchmore.json
    const punchmoreData = ConfigLoader.getAttackConfig('punchmore') as Record<string, unknown> | undefined;
    this.config = ((punchmoreData as Record<string, unknown>)?.['glow'] as GlowConfig | undefined) ?? {
      radius: 18, color: '0x44aaff', alpha: 0.35,
    };

    events.on('attack-state-changed', this.onAttackStateChanged, this);
  }

  private onAttackStateChanged(state: AttackState, attackType: string): void {
    if (attackType !== 'punchmore') {
      this.targetAlpha = 0;
      return;
    }

    switch (state) {
      case 'active':
        this.targetAlpha = this.config.alpha;
        break;
      case 'windup':
      case 'recovery':
      case 'idle':
        this.targetAlpha = 0;
        break;
    }
  }

  update(delta: number): void {
    // Snap to full on active, fade out over 200ms
    if (this.targetAlpha > 0) {
      this.currentAlpha = this.targetAlpha;
    } else if (this.currentAlpha > this.targetAlpha) {
      this.currentAlpha = Math.max(this.currentAlpha - this.config.alpha * (delta / 200), 0);
    }

    if (this.currentAlpha <= 0.01) {
      if (this.glowImage) this.glowImage.setVisible(false);
      for (const s of this.spikes) s.image.setVisible(false);
      this.currentAlpha = 0;
      return;
    }

    this.elapsed += delta / 1000;

    // Create glow image lazily
    if (!this.glowImage) {
      const texKey = ensureSoftCircleTexture(this.scene, this.config.radius * CRISP_SCALE);
      this.glowImage = this.scene.add.image(0, 0, texKey);
      this.glowImage.setScale(INV_CRISP);
      this.glowImage.setTint(parseInt(this.config.color.replace('0x', ''), 16));
      this.glowImage.setBlendMode(Phaser.BlendModes.ADD);
    }

    // Create spikes lazily alongside the glow
    const sp = this.config.spikes;
    if (sp && this.spikes.length === 0) {
      const tint = parseInt(this.config.color.replace('0x', ''), 16);
      const spikeTex = ensureSpikeTexture(this.scene, sp.width, sp.texHeight);
      for (let i = 0; i < sp.count; i++) {
        const baseAngle = (i / sp.count) * Math.PI;
        const length = sp.minLength + Math.random() * (sp.maxLength - sp.minLength);
        const img = this.scene.add.image(0, 0, spikeTex);
        img.setTint(tint);
        img.setBlendMode(Phaser.BlendModes.ADD);
        img.setVisible(false);
        this.spikes.push({
          image: img,
          baseAngle,
          scaleY: length / sp.texHeight,
          shimmerPhase: Math.random() * Math.PI * 2,
        });
      }
    }

    // Fade progress: 1 at full glow, 0 at fully faded.
    // Scale contracts the radius so the radial gradient's outer rim
    // dissolves first — the glow shrinks inward as it dissipates.
    const t = this.currentAlpha / this.config.alpha;
    const glowScale = INV_CRISP * (0.4 + 0.6 * t);
    this.glowImage.setScale(glowScale);

    const { x: cx, y: cy, nx, ny } = getFistPosition(this.sprite.x, this.sprite.y, this.facing);
    const isHorizontal = Math.abs(nx) > Math.abs(ny);

    this.glowImage.x = cx;
    this.glowImage.y = cy;
    this.glowImage.setAlpha(this.currentAlpha);
    this.glowImage.setVisible(true);
    this.glowImage.setDepth(this.sprite.depth - 1);

    // Spikes ride the same position, spinning slowly with shimmer modulation
    if (sp) {
      const spinOffset = this.elapsed * sp.spinSpeed * Math.PI * 2;
      for (const s of this.spikes) {
        const shimmer = 1 - sp.shimmerDepth * (0.5 + 0.5 * Math.sin(
          s.shimmerPhase + this.elapsed * sp.shimmerHz * Math.PI * 2,
        ));
        s.image.x = cx;
        s.image.y = cy;
        s.image.setRotation(s.baseAngle + spinOffset);
        s.image.setScale(1, s.scaleY * t);
        s.image.setAlpha(sp.alpha * t * shimmer);
        s.image.setVisible(true);
        s.image.setDepth(this.sprite.depth - 1);
      }
    }
  }

  destroy(): void {
    if (this.glowImage) {
      this.glowImage.destroy();
      this.glowImage = null;
    }
    for (const s of this.spikes) s.image.destroy();
    this.spikes = [];
  }
}
